import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { Box } from '@mui/material';
import YouTubePlayer, { type YouTubePlayerHandle } from '@/components/YouTubePlayer';

const LOUPE_RADIUS = 115;
const LOUPE_DIAMETER = LOUPE_RADIUS * 2;
const LOUPE_CLIP_ID = 'video-magnifier-loupe-clip';
const TAP_MOVE_PX = 10;
const TAP_MAX_MS = 400;

const loupeClipSx = {
  clipPath: `url(#${LOUPE_CLIP_ID})`,
  WebkitClipPath: `url(#${LOUPE_CLIP_ID})`,
  maskImage: 'radial-gradient(circle closest-side, #000 99.5%, transparent 100%)',
  WebkitMaskImage: 'radial-gradient(circle closest-side, #000 99.5%, transparent 100%)',
  maskRepeat: 'no-repeat',
  WebkitMaskRepeat: 'no-repeat',
  maskSize: '100% 100%',
  WebkitMaskSize: '100% 100%',
  maskPosition: 'center',
  WebkitMaskPosition: 'center',
} as const;

interface Props {
  enabled: boolean;
  zoom: number;
  children: ReactNode;
  videoId?: string | null;
  playbackRate?: number;
  isPlaying?: boolean;
  getSyncTime?: () => number;
  onTogglePlay?: () => void;
  onZoomChange?: (zoom: number) => void;
  zoomMin?: number;
  zoomMax?: number;
  zoomStep?: number;
}

/** Circular loupe — main video stays normal; magnified view only inside the ring. */
export default function VideoMagnifierOverlay({
  enabled,
  zoom,
  children,
  videoId,
  playbackRate = 1,
  isPlaying = false,
  getSyncTime,
  onTogglePlay,
  onZoomChange,
  zoomMin = 1.5,
  zoomMax = 4,
  zoomStep = 0.5,
}: Props) {
  const containerRef = useRef<HTMLDivElement>(null);
  const loupePlayerRef = useRef<YouTubePlayerHandle>(null);
  const pointerStartRef = useRef<{ x: number; y: number; t: number } | null>(null);
  const primeTimerRef = useRef<number | null>(null);
  const [focal, setFocal] = useState<{ x: number; y: number } | null>(null);
  const [size, setSize] = useState({ w: 0, h: 0 });
  const [loupeReady, setLoupeReady] = useState(false);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const measure = () => setSize({ w: el.clientWidth, h: el.clientHeight });
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  useEffect(() => {
    if (!enabled) {
      setFocal(null);
      setLoupeReady(false);
    }
  }, [enabled]);

  useEffect(() => {
    return () => {
      if (primeTimerRef.current != null) window.clearTimeout(primeTimerRef.current);
    };
  }, []);

  const clearPrimeTimer = useCallback(() => {
    if (primeTimerRef.current != null) {
      window.clearTimeout(primeTimerRef.current);
      primeTimerRef.current = null;
    }
  }, []);

  const primeLoupeFrame = useCallback(() => {
    const t = getSyncTime?.() ?? 0;
    loupePlayerRef.current?.seekTo(t);
    if (isPlaying) {
      loupePlayerRef.current?.play();
      return;
    }
    clearPrimeTimer();
    loupePlayerRef.current?.play();
    primeTimerRef.current = window.setTimeout(() => {
      loupePlayerRef.current?.seekTo(t);
      loupePlayerRef.current?.pause();
      primeTimerRef.current = null;
    }, 120);
  }, [clearPrimeTimer, getSyncTime, isPlaying]);

  const handleLoupeReady = useCallback(() => {
    setLoupeReady(true);
    primeLoupeFrame();
  }, [primeLoupeFrame]);

  const updateFocal = useCallback((clientX: number, clientY: number) => {
    const el = containerRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    setFocal({
      x: clientX - rect.left,
      y: clientY - rect.top,
    });
  }, []);

  const handlePointerDown = useCallback((e: React.PointerEvent) => {
    pointerStartRef.current = { x: e.clientX, y: e.clientY, t: Date.now() };
  }, []);

  const handlePointerMove = useCallback(
    (e: React.PointerEvent) => {
      updateFocal(e.clientX, e.clientY);
    },
    [updateFocal]
  );

  const handlePointerEnter = useCallback(
    (e: React.PointerEvent) => {
      updateFocal(e.clientX, e.clientY);
    },
    [updateFocal]
  );

  const handlePointerLeave = useCallback(() => {
    setFocal(null);
    pointerStartRef.current = null;
  }, []);

  const handlePointerUp = useCallback(
    (e: React.PointerEvent) => {
      const start = pointerStartRef.current;
      pointerStartRef.current = null;
      if (!start || !onTogglePlay) return;
      const dx = e.clientX - start.x;
      const dy = e.clientY - start.y;
      if (dx * dx + dy * dy <= TAP_MOVE_PX * TAP_MOVE_PX && Date.now() - start.t <= TAP_MAX_MS) {
        onTogglePlay();
      }
    },
    [onTogglePlay]
  );

  useEffect(() => {
    if (!enabled || !onZoomChange) return;
    const el = containerRef.current;
    if (!el) return;
    const onWheel = (e: WheelEvent) => {
      e.preventDefault();
      const direction = e.deltaY < 0 ? 1 : e.deltaY > 0 ? -1 : 0;
      if (direction === 0) return;
      const next = Math.min(
        zoomMax,
        Math.max(zoomMin, Math.round((zoom + direction * zoomStep) * 2) / 2)
      );
      if (next !== zoom) onZoomChange(next);
    };
    el.addEventListener('wheel', onWheel, { passive: false });
    return () => el.removeEventListener('wheel', onWheel);
  }, [enabled, onZoomChange, zoom, zoomMin, zoomMax, zoomStep]);

  useEffect(() => {
    if (!enabled || !loupeReady || !getSyncTime) return;
    const sync = () => {
      const t = getSyncTime();
      loupePlayerRef.current?.seekTo(t);
    };
    sync();
    const id = window.setInterval(sync, 200);
    return () => window.clearInterval(id);
  }, [enabled, loupeReady, getSyncTime, zoom]);

  useEffect(() => {
    if (!enabled || !loupeReady) return;
    if (isPlaying) {
      clearPrimeTimer();
      loupePlayerRef.current?.play();
      return;
    }
    primeLoupeFrame();
  }, [clearPrimeTimer, enabled, loupeReady, isPlaying, primeLoupeFrame]);

  const loupeMounted = enabled && !!videoId && size.w > 0 && size.h > 0;
  const showLoupe = loupeMounted && focal && loupeReady;
  const loupeLeft = focal ? focal.x - LOUPE_RADIUS : 0;
  const loupeTop = focal ? focal.y - LOUPE_RADIUS : 0;

  return (
    <Box
      ref={containerRef}
      sx={{
        position: 'relative',
        width: '100%',
        height: '100%',
        overflow: 'hidden',
      }}
    >
      <svg aria-hidden width={0} height={0} style={{ position: 'absolute' }}>
        <defs>
          <clipPath id={LOUPE_CLIP_ID} clipPathUnits="objectBoundingBox">
            <circle cx="0.5" cy="0.5" r="0.5" />
          </clipPath>
        </defs>
      </svg>

      <Box sx={{ width: '100%', height: '100%', pointerEvents: enabled ? 'none' : 'auto' }}>{children}</Box>

      {enabled && (
        <Box
          onPointerDown={handlePointerDown}
          onPointerMove={handlePointerMove}
          onPointerEnter={handlePointerEnter}
          onPointerLeave={handlePointerLeave}
          onPointerUp={handlePointerUp}
          sx={{
            position: 'absolute',
            inset: 0,
            zIndex: 5,
            cursor: onTogglePlay ? 'pointer' : 'crosshair',
            touchAction: 'none',
          }}
        />
      )}

      {loupeMounted && (
        <Box
          sx={{
            position: 'absolute',
            left: showLoupe ? loupeLeft : -LOUPE_DIAMETER * 2,
            top: showLoupe ? loupeTop : 0,
            width: LOUPE_DIAMETER,
            height: LOUPE_DIAMETER,
            borderRadius: '50%',
            overflow: 'hidden',
            isolation: 'isolate',
            transform: 'translateZ(0)',
            zIndex: 6,
            pointerEvents: 'none',
            visibility: showLoupe ? 'visible' : 'hidden',
            ...loupeClipSx,
          }}
        >
          <Box
            sx={{
              position: 'absolute',
              width: size.w,
              height: size.h,
              transform: `scale(${zoom})`,
              transformOrigin: '0 0',
              left: focal ? LOUPE_RADIUS - focal.x * zoom : 0,
              top: focal ? LOUPE_RADIUS - focal.y * zoom : 0,
              willChange: 'transform',
            }}
          >
            <Box
              sx={{
                width: size.w,
                height: size.h,
                '& iframe': {
                  pointerEvents: 'none',
                },
              }}
            >
              <YouTubePlayer
                ref={loupePlayerRef}
                videoId={videoId}
                playbackRate={playbackRate}
                initialTime={getSyncTime?.() ?? 0}
                fillContainer
                muted
                hideControls
                suppressLoadingUI
                onReady={handleLoupeReady}
              />
            </Box>
          </Box>
        </Box>
      )}

      {showLoupe && (
        <Box
          aria-hidden
          sx={{
            position: 'absolute',
            left: loupeLeft,
            top: loupeTop,
            width: LOUPE_DIAMETER,
            height: LOUPE_DIAMETER,
            borderRadius: '50%',
            zIndex: 7,
            pointerEvents: 'none',
            border: '2px solid rgba(255,255,255,0.92)',
            boxShadow: '0 4px 18px rgba(0,0,0,0.45), 0 0 0 1px rgba(0,0,0,0.25)',
          }}
        />
      )}
    </Box>
  );
}
